import { PageContainer, ProCard } from '@ant-design/pro-components';
import { Line } from '@ant-design/charts';
import { Row, Col, DatePicker, Space, Card, Descriptions, Tag, Spin, message, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState, useMemo } from 'react';
import { useModel } from '@umijs/max';
import { getApiTestHealth } from '@/services/api/test';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;

type SalesEntry = { date: string; sales: number };
type WeeklyEntry = { week: string; sales: number };
type MonthlyEntry = { month: string; sales: number };
type ShipmentEntry = { type: string; value: number };

type HealthCheckEntryDto = {
  name: string;
  status: string;
  durationMs?: number;
  error?: string;
};

const Dashboard = () => {
  const { initialState } = useModel('@@initialState');

  const [dailySales, setDailySales] = useState<SalesEntry[]>([]);
  const [weeklySales, setWeeklySales] = useState<WeeklyEntry[]>([]);
  const [monthlySales, setMonthlySales] = useState<MonthlyEntry[]>([]);
  const [shipmentStats, setShipmentStats] = useState<ShipmentEntry[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState<any>(null);

  const fetchHealthStatus = async () => {
    try {
      setLoading(true);
    
      const response = await getApiTestHealth();
      if (response.success) {
        setHealthData(response);
        message.success('Dashboard data refreshed successfully!');
      } else {
        message.error(response.message || 'Failed to fetch health status.');
      }
    } catch (error) {
      message.error('API request failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDailySales([
      { date: '2025-04-25', sales: 1200 },
      { date: '2025-04-26', sales: 1500 },
      { date: '2025-04-27', sales: 1800 },
      { date: '2025-04-28', sales: 1700 },
      { date: '2025-04-29', sales: 2000 },
      { date: '2025-04-30', sales: 2100 },
      { date: '2025-05-01', sales: 1900 },
    ]);

    setWeeklySales([
      { week: '2025-W14', sales: 8500 },
      { week: '2025-W15', sales: 9200 },
      { week: '2025-W16', sales: 9600 },
      { week: '2025-W17', sales: 10200 },
      { week: '2025-W18', sales: 9800 },
      { week: '2025-W19', sales: 11000 },
    ]);

    setMonthlySales([
      { month: '2024-06', sales: 12500 },
      { month: '2024-07', sales: 13100 },
      { month: '2024-08', sales: 14500 },
      { month: '2024-09', sales: 15300 },
      { month: '2024-10', sales: 14800 },
      { month: '2024-11', sales: 16200 },
      { month: '2024-12', sales: 17400 },
      { month: '2025-01', sales: 16900 },
      { month: '2025-02', sales: 18500 },
      { month: '2025-03', sales: 19200 },
      { month: '2025-04', sales: 19800 },
      { month: '2025-05', sales: 20500 },
    ]);

    setShipmentStats([
      { type: 'Shipped', value: 4800 },
      { type: 'Pending', value: 1200 },
      { type: 'In Stock', value: 3000 },
    ]);

    fetchHealthStatus();
  }, []);

  useEffect(() => {
    return () => {
      // Removed logging for component unmount
    };
  }, []);

  const filteredDailySales = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return dailySales;
    const [start, end] = dateRange;
    return dailySales.filter((entry) => {
      const date = dayjs(entry.date);
      return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
    });
  }, [dailySales, dateRange]);

  const filteredWeeklySales = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return weeklySales;
    const [start, end] = dateRange;
    return weeklySales.filter((entry) => {
      const weekStart = dayjs(entry.week + '-1');
      return weekStart.isSameOrAfter(start, 'week') && weekStart.isSameOrBefore(end, 'week');
    });
  }, [weeklySales, dateRange]);

  const filteredMonthlySales = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return monthlySales;
    const [start, end] = dateRange;
    return monthlySales.filter((entry) => {
      const month = dayjs(entry.month);
      return month.isSameOrAfter(start, 'month') && month.isSameOrBefore(end, 'month');
    });
  }, [monthlySales, dateRange]);

  const lineConfig = (data: any[], xField: string, yField: string) => ({
    data,
    xField,
    yField,
    point: { size: 4, shape: 'diamond' },
    meta: {
      [xField]: { alias: 'Time' },
      [yField]: { alias: 'Sales' },
    },
    height: 250,
    slider: {
      start: 0,
      end: 1,
    },
  });

  const renderStatusTag = (status: string) => {
    if (status === 'Healthy') {
      return <Tag color="green">Healthy</Tag>;
    }
    return <Tag color="red">{status === 'Unhealthy' ? 'Unhealthy' : (status || 'Unhealthy')}</Tag>;
  };

  // Filter health checks to only show ActivataDB and BillingDB
  const filterHealthChecks = (checks: HealthCheckEntryDto[]) => {
    if (!checks) return [];
    return checks.filter(check => 
      check.name?.includes('ActivataDB') || check.name?.includes('BillingDB')
    );
  };

  // Filter external checks - show website checks but exclude BonfireDB and UPMDB
  const filterExternalChecks = (checks: HealthCheckEntryDto[]) => {
    if (!checks) return [];
    return checks.filter(check => 
      !check.name?.includes('BonfireDB') && !check.name?.includes('UPMDB')
    );
  };

  return (
    <PageContainer 
      breadcrumbRender={false}
      extra={[
        <Button 
          key="refresh"
          type="primary" 
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={fetchHealthStatus}
        >
          Refresh
        </Button>
      ]}
    >
      <Card
        style={{ borderRadius: 8, marginBottom: 24 }}
        styles={{
          body: {
            backgroundImage:
              initialState?.settings?.navTheme === 'realDark'
                ? 'linear-gradient(75deg, #1A1B1F 0%, #191C1F 100%)'
                : 'linear-gradient(75deg, #FBFDFF 0%, #F5F7FF 100%)',
          },
        }}
      >
        <div
          style={{
            paddingBottom: 24,
          }}
        >
          {loading ? (
            <Spin tip="Loading health status..." />
          ) : healthData?.data ? (
            <Row gutter={[24, 24]}>
              {/* Internal Checks */}
              <Col span={12}>
                <Card 
                  title="Internal System Checks" 
                  size="small"
                  style={{ height: '100%' }}
                  headStyle={{ 
                    background: '#f6ffed', 
                    borderBottom: '1px solid #b7eb8f',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {(() => {
                    const filteredInternalChecks = filterHealthChecks(healthData.data.internalChecks || []);
                    return filteredInternalChecks.length ? (
                      <Descriptions bordered column={1} size="small">
                        {filteredInternalChecks.map((check: HealthCheckEntryDto) => (
                          <Descriptions.Item label={check.name} key={check.name}>
                            <Space direction="vertical" size="small">
                              <Space>
                                {renderStatusTag(check.status)}
                                <span>Response Time: {check.durationMs?.toFixed(2) ?? '--'} ms</span>
                              </Space>
                              {check.error && (
                                <span style={{ color: 'red', fontSize: '12px' }}>
                                  ⚠ {check.error}
                                </span>
                              )}
                            </Space>
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                        No internal check data available
                      </div>
                    );
                  })()}
                </Card>
              </Col>

              {/* External Checks */}
              <Col span={12}>
                <Card 
                  title="External Service Checks" 
                  size="small"
                  style={{ height: '100%' }}
                  headStyle={{ 
                    background: '#fff1f0', 
                    borderBottom: '1px solid #ffadd2',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {(() => {
                    const filteredExternalChecks = filterExternalChecks(healthData.data.externalChecks || []);
                    return filteredExternalChecks.length ? (
                      <Descriptions bordered column={1} size="small">
                        {filteredExternalChecks.map((check: HealthCheckEntryDto) => (
                          <Descriptions.Item label={check.name} key={check.name}>
                            <Space direction="vertical" size="small">
                              <Space>
                                {renderStatusTag(check.status)}
                                <span>Response Time: {check.durationMs?.toFixed(2) ?? '--'} ms</span>
                              </Space>
                              {check.error && (
                                <span style={{ color: 'red', fontSize: '12px' }}>
                                  ⚠ {check.error}
                                </span>
                              )}
                            </Space>
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                        No external check data available
                      </div>
                    );
                  })()}
                </Card>
              </Col>
            </Row>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              No health check data available
            </div>
          )}
        </div>
      </Card>

      {/* <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row justify="start" style={{ marginBottom: 8 }}>
            <Col>
              <Space>
                <span style={{ fontWeight: 500 }}>Date Range:</span>
                <RangePicker onChange={(dates) => setDateRange(dates ?? null)} />
              </Space>
            </Col>
          </Row>

          <ProCard title="Sales - Last 7 Days" bordered>
            <Line {...lineConfig(filteredDailySales, 'date', 'sales')} />
          </ProCard>
        </Col>
        <Col span={12}>
          <ProCard title="Sales by Week" bordered>
            <Line {...lineConfig(filteredWeeklySales, 'week', 'sales')} />
          </ProCard>
        </Col>
        <Col span={12}>
          <ProCard title="Sales by Month" bordered>
            <Line {...lineConfig(filteredMonthlySales, 'month', 'sales')} />
          </ProCard>
        </Col>
        <Col span={24}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            {shipmentStats.map((item) => (
              <Col span={8} key={item.type}>
                <Card variant="outlined" style={{ textAlign: 'center' }}>
                  <h3 style={{ marginBottom: 8 }}>{item.type}</h3>
                  <span style={{ fontSize: 24, fontWeight: 'bold' }}>{item.value}</span>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row> */}
    </PageContainer>
  );
};

export default Dashboard;
